import { z } from "zod";

/**
 * Contrat du module Livres-jeux de la console (spec du 03/10/2026,
 * `docs/specs/2026-10-03-console-livre-jeu-design.md`, chapeau #276 ; ADR 0012).
 *
 * L'editeur du livre-jeu : un livre (titre, depart, indices, jauges, trame) et ses scenes
 * (titre, texte, page imposee, fin, choix ecrits en YAML), ecrits par Nicolas dans la console
 * ou par Claude avec le jeton du module, verifies, mis en page et publies vers l'appli
 * compagnon par le service `livre-jeu-moteur` de fortress. La console ne lit pas le YAML : le
 * moteur est seul juge du format, et ce contrat ne dit que ce que la console garde et montre.
 *
 * Les cles JSON sont en `snake_case`, comme celles des autres modules a tables. Chaque nom
 * porte `Jeu` : le pont `contract.ts` reexporte tous les modules d'un bloc, et les Histoires
 * ont deja un `livreSchema`. Les enumerations disent exactement ce que disent les contraintes
 * CHECK de `migrations/1005_livres_jeux.sql` : un test du backend les compare.
 */

const identifiant = z.number().int().positive();
const compte = z.number().int().nonnegative();
/** Une version : augmente a chaque ecriture, 409 sur une ecriture qui en porte une autre. */
const version = z.number().int().nonnegative();
/** Une date rendue par la console, en ISO 8601. */
const instant = z.string();

/** L'identifiant d'un livre, d'une scene, d'un indice ou d'une jauge, au format des histoires. */
export const CLE_JEU = /^[a-z0-9][a-z0-9-]{0,59}$/;
export const cleJeuSchema = z
  .string()
  .regex(CLE_JEU, "minuscules, chiffres et tirets seulement, 60 caractères au plus");

/** Le titre d'une scene, le libelle d'un indice ou d'une jauge. */
export const TITRE_JEU_MAX = 200;
export const LIBELLE_JEU_MAX = 120;
/** Le titre d'un livre : l'appli compagnon n'en prend pas davantage (`apps/livre-jeu`). */
export const TITRE_LIVRE_JEU_MAX = 160;
export const TRAME_MAX = 20_000;
export const TEXTE_SCENE_MAX = 20_000;
export const CHOIX_YAML_MAX = 20_000;
/**
 * Les bornes du moteur (`LIMITES` de `packages/livre-jeu/src/sauvegarde.ts`) : le code de
 * sauvegarde tient sur 40 bits (rang de la scene sur 8, un bit par indice sur 12, trois jauges
 * de 0 a 15). Un livre court a 80 a 120 scenes.
 */
export const SCENES_JEU_MAX = 256;
export const INDICES_JEU_MAX = 12;
export const JAUGES_JEU_MAX = 3;
export const JAUGE_JEU_MAX = 15;
/**
 * Un livre entier en YAML, en caracteres : un chapitre de vingt scenes en fait une quarantaine
 * de milliers, un livre court de cent vingt environ deux cent cinquante mille. Au pire du JSON
 * (6 octets par caractere), le corps reste sous les 2 Mo du frontal.
 */
export const YAML_LIVRE_MAX = 300_000;
/** Le domaine de l'appli compagnon, ou menent les QR codes. */
export const DOMAINE_JEU_DEFAUT = "livre-jeu.petitlab.fr";
export const DOMAINE_JEU = /^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}$/;

/** Qui a ecrit le texte d'une scene ou la trame : l'un, l'autre, ou les deux tour a tour. */
export const origineJeuSchema = z.enum(["nicolas", "claude", "les-deux"]);
export type OrigineJeu = z.infer<typeof origineJeuSchema>;

/** Qui a fait une ecriture : Nicolas par sa session, Claude par le jeton du module. */
export const auteurJeuSchema = z.enum(["nicolas", "claude"]);
export type AuteurJeu = z.infer<typeof auteurJeuSchema>;

export const finJeuSchema = z.enum(["reussie", "ratee"]);
export type FinJeu = z.infer<typeof finJeuSchema>;

/** Ce que note le journal du livre. */
export const quoiJournalJeuSchema = z.enum([
  "livre_cree",
  "trame",
  "reglages",
  "scene_creee",
  "scene_supprimee",
  "ordre",
  "texte",
  "structure",
  "import",
  "publication",
]);
export type QuoiJournalJeu = z.infer<typeof quoiJournalJeuSchema>;

const titre = z.string().trim().min(1).max(TITRE_LIVRE_JEU_MAX);
const libelle = z.string().trim().min(1).max(LIBELLE_JEU_MAX);

export const indiceJeuSchema = z.strictObject({ id: cleJeuSchema, libelle });
export type IndiceJeu = z.infer<typeof indiceJeuSchema>;

export const jaugeJeuSchema = z
  .strictObject({
    id: cleJeuSchema,
    libelle,
    max: z.number().int().min(1).max(JAUGE_JEU_MAX),
    depart: z.number().int().min(0).max(JAUGE_JEU_MAX),
  })
  .refine((jauge) => jauge.depart <= jauge.max, {
    message: "le départ d'une jauge ne dépasse pas son maximum",
    path: ["depart"],
  });
export type JaugeJeu = z.infer<typeof jaugeJeuSchema>;

/** Les reglages d'un livre, tels que le formulaire les ecrit. */
export const reglagesJeuSchema = z.object({
  titre,
  domaine: z.string().max(100).regex(DOMAINE_JEU, "un domaine, par exemple livre-jeu.petitlab.fr"),
  depart: cleJeuSchema.nullable(),
  indices: z.array(indiceJeuSchema).max(INDICES_JEU_MAX),
  jauges: z.array(jaugeJeuSchema).max(JAUGES_JEU_MAX),
});
export type ReglagesJeu = z.infer<typeof reglagesJeuSchema>;

// ---------------------------------------------------------------------------------------
// Les livres

export const publicationJeuSchema = z.object({
  id: identifiant,
  empreinte: z.string(),
  structure: z.string(),
  publie_le: instant,
  /** L'appli a pris le livre (204) ; sinon `reponse` dit pourquoi. */
  recu: z.boolean(),
  reponse: z.string().nullable(),
});
export type PublicationJeu = z.infer<typeof publicationJeuSchema>;

export const livreJeuResumeSchema = z.object({
  id: identifiant,
  cle: cleJeuSchema,
  titre: z.string(),
  scenes: compte,
  /** Les scenes qui ont un texte. */
  ecrites: compte,
  maj_le: instant,
  /** La derniere publication vers l'appli, ou `null`. */
  publication: publicationJeuSchema.nullable(),
});
export type LivreJeuResume = z.infer<typeof livreJeuResumeSchema>;

export const listeLivresJeuSchema = z.object({ livres: z.array(livreJeuResumeSchema) });
export type ListeLivresJeu = z.infer<typeof listeLivresJeuSchema>;

export const creerLivreJeuSchema = z.strictObject({ cle: cleJeuSchema, titre });
export type CreerLivreJeu = z.infer<typeof creerLivreJeuSchema>;

export const livreJeuSchema = reglagesJeuSchema.extend({
  id: identifiant,
  cle: cleJeuSchema,
  reglages_version: version,
  trame: z.string(),
  trame_version: version,
  trame_provenance: origineJeuSchema.nullable(),
  trame_derniere_origine: auteurJeuSchema.nullable(),
  cree_le: instant,
  maj_le: instant,
});
export type LivreJeu = z.infer<typeof livreJeuSchema>;

export const livreJeuCreeSchema = z.object({ livre: livreJeuSchema });
/** Les reglages ecrits : le livre tel qu'il est maintenant. */
export const reglagesEcritsJeuSchema = livreJeuCreeSchema;

/** Un choix tel que l'apercu du livre le montre : ou il mene. */
export const choixJeuResumeSchema = z.object({
  id: z.string(),
  libelle: z.string(),
  /** Les scenes visees par ses suites, ou par les deux issues de son enigme. */
  cibles: z.array(z.string()),
  enigme: z.boolean(),
});
export type ChoixJeuResume = z.infer<typeof choixJeuResumeSchema>;

export const sceneJeuResumeSchema = z.object({
  id: identifiant,
  rang: z.number().int().positive(),
  cle: cleJeuSchema,
  titre: z.string().nullable(),
  /** Le debut du texte, pour reconnaitre la scene. */
  extrait: z.string(),
  mots: compte,
  provenance: origineJeuSchema.nullable(),
  page: z.number().int().positive().nullable(),
  fin: finJeuSchema.nullable(),
  choix: z.array(choixJeuResumeSchema),
  structure_version: version,
  texte_version: version,
});
export type SceneJeuResume = z.infer<typeof sceneJeuResumeSchema>;

export const ligneJournalJeuSchema = z.object({
  id: identifiant,
  quoi: quoiJournalJeuSchema,
  par: auteurJeuSchema,
  scene_cle: z.string().nullable(),
  le: instant,
});
export type LigneJournalJeu = z.infer<typeof ligneJournalJeuSchema>;

export const livreJeuDetailSchema = z.object({
  livre: livreJeuSchema,
  scenes: z.array(sceneJeuResumeSchema),
  publication: publicationJeuSchema.nullable(),
  /** Les dernieres ecritures, la plus recente en tete. */
  journal: z.array(ligneJournalJeuSchema),
});
export type LivreJeuDetail = z.infer<typeof livreJeuDetailSchema>;

/** Les reglages ecrits, avec la version qu'ils remplacent. */
export const ecrireReglagesJeuSchema = z.strictObject({
  titre,
  domaine: reglagesJeuSchema.shape.domaine,
  depart: cleJeuSchema.nullable(),
  indices: reglagesJeuSchema.shape.indices,
  jauges: reglagesJeuSchema.shape.jauges,
  version,
});
export type EcrireReglagesJeu = z.infer<typeof ecrireReglagesJeuSchema>;

// ---------------------------------------------------------------------------------------
// Le texte : la trame d'un livre et le texte d'une scene, ecrits par la sauvegarde
// automatique, a la version qu'ils remplacent.

export const ecrireTexteJeuSchema = z.strictObject({
  texte: z.string().max(Math.max(TRAME_MAX, TEXTE_SCENE_MAX)),
  version,
});
export type EcrireTexteJeu = z.infer<typeof ecrireTexteJeuSchema>;

/** Une ecriture de texte confirmee. `provenance` : qui a ecrit le texte maintenant en base. */
export const texteEcritJeuSchema = z.object({
  texte_version: version,
  provenance: origineJeuSchema.nullable(),
  maj_le: instant,
});
export type TexteEcritJeu = z.infer<typeof texteEcritJeuSchema>;

/** Le 409 d'une ecriture de texte : le texte courant, et qui l'a ecrit en dernier. */
export const conflitTexteJeuSchema = z.object({
  error: z.object({
    code: z.literal("texte_modifie"),
    message: z.string(),
    requestId: z.string().optional(),
  }),
  courant: z.object({
    texte: z.string().nullable(),
    provenance: origineJeuSchema.nullable(),
    texte_version: version,
    derniere_origine: auteurJeuSchema.nullable(),
  }),
});
export type ConflitTexteJeu = z.infer<typeof conflitTexteJeuSchema>;

// ---------------------------------------------------------------------------------------
// Les scenes

export const nouvelleSceneJeuSchema = z.strictObject({
  cle: cleJeuSchema,
  titre: z.string().trim().max(TITRE_JEU_MAX).nullable().optional(),
});
export type NouvelleSceneJeu = z.infer<typeof nouvelleSceneJeuSchema>;

export const sceneCreeeJeuSchema = z.object({ scene: sceneJeuResumeSchema });

/** Les scenes d'un livre dans leur nouvel ordre, apres un deplacement ou une suppression. */
export const scenesJeuSchema = z.object({ scenes: z.array(sceneJeuResumeSchema) });
export type ScenesJeu = z.infer<typeof scenesJeuSchema>;

/** Supprimer une scene : rien a dire de plus que son identifiant, dans le chemin. */
export const supprimerSceneJeuSchema = z.strictObject({});

/** Le nouvel ordre des scenes : tous leurs identifiants, chacun une fois. */
export const ordreScenesJeuSchema = z.strictObject({
  scenes: z.array(identifiant).min(1).max(SCENES_JEU_MAX),
});
export type OrdreScenesJeu = z.infer<typeof ordreScenesJeuSchema>;

/** La structure d'une scene : tout sauf son texte, a la version qu'elle remplace. */
export const ecrireStructureJeuSchema = z.strictObject({
  cle: cleJeuSchema,
  titre: z.string().trim().max(TITRE_JEU_MAX).nullable(),
  page: z.number().int().min(1).max(999).nullable(),
  fin: finJeuSchema.nullable(),
  choix_yaml: z.string().max(CHOIX_YAML_MAX),
  version,
});
export type EcrireStructureJeu = z.infer<typeof ecrireStructureJeuSchema>;

/** Le 400 d'une structure dont les choix ne se lisent pas : les fautes du moteur. */
export const choixIllisiblesJeuSchema = z.object({
  error: z.object({
    code: z.literal("choix_illisibles"),
    message: z.string(),
    requestId: z.string().optional(),
  }),
  erreurs: z.array(z.string()),
});
export type ChoixIllisiblesJeu = z.infer<typeof choixIllisiblesJeuSchema>;

export const sceneJeuSchema = z.object({
  id: identifiant,
  livre_id: identifiant,
  rang: z.number().int().positive(),
  cle: cleJeuSchema,
  titre: z.string().nullable(),
  texte: z.string(),
  texte_version: version,
  provenance: origineJeuSchema.nullable(),
  derniere_origine: auteurJeuSchema.nullable(),
  page: z.number().int().positive().nullable(),
  fin: finJeuSchema.nullable(),
  choix_yaml: z.string(),
  structure_version: version,
  maj_le: instant,
});
export type SceneJeu = z.infer<typeof sceneJeuSchema>;

/** La structure ecrite : la scene telle qu'elle est maintenant. */
export const structureEcriteJeuSchema = z.object({ scene: sceneJeuSchema });

/** Une scene et ce qu'il faut du livre pour l'ecrire : le pense-bete et les voisines. */
export const sceneJeuDetailSchema = z.object({
  scene: sceneJeuSchema,
  livre: z.object({
    id: identifiant,
    cle: cleJeuSchema,
    titre: z.string(),
    depart: cleJeuSchema.nullable(),
    indices: z.array(indiceJeuSchema),
    jauges: z.array(
      z.object({ id: z.string(), libelle: z.string(), max: z.number(), depart: z.number() }),
    ),
    scenes: z.array(
      z.object({
        id: identifiant,
        rang: z.number().int().positive(),
        cle: cleJeuSchema,
        titre: z.string().nullable(),
      }),
    ),
  }),
});
export type SceneJeuDetail = z.infer<typeof sceneJeuDetailSchema>;

// ---------------------------------------------------------------------------------------
// La verification, rendue par le moteur, chaque faute rattachee a sa scene quand elle en
// nomme une.

export const fauteJeuSchema = z.object({
  message: z.string(),
  scene_id: identifiant.nullable(),
  scene_cle: z.string().nullable(),
});
export type FauteJeu = z.infer<typeof fauteJeuSchema>;

export const statsJeuSchema = z.object({
  scenes: compte,
  choix: compte,
  etats: compte,
  fins: z.record(z.string(), compte),
  plus_court: z.record(z.string(), compte),
});
export type StatsJeu = z.infer<typeof statsJeuSchema>;

export const rapportJeuSchema = z.object({
  ok: z.boolean(),
  erreurs: z.array(fauteJeuSchema),
  avertissements: z.array(fauteJeuSchema),
  stats: statsJeuSchema.nullable(),
});
export type RapportJeu = z.infer<typeof rapportJeuSchema>;

/** Le 422 d'un livre que le moteur ne peut ni mettre en page ni publier : le rapport. */
export const livreInvalideJeuSchema = z.object({
  error: z.object({
    code: z.literal("livre_invalide"),
    message: z.string(),
    requestId: z.string().optional(),
  }),
  rapport: rapportJeuSchema,
});
export type LivreInvalideJeu = z.infer<typeof livreInvalideJeuSchema>;

// ---------------------------------------------------------------------------------------
// La mise en page, rendue par le moteur ; rien n'est garde dans la console.

export const miseEnPageJeuSchema = z.object({
  /** Le PDF, en base64. */
  pdf: z.string(),
  pages: compte,
  /** Les scenes qui debordent de leur page, telles que le moteur les nomme. */
  decalages: z.array(z.string()),
});
export type MiseEnPageJeu = z.infer<typeof miseEnPageJeuSchema>;

// ---------------------------------------------------------------------------------------
// Les textes d'avant : ce que le journal garde d'un texte remplace

/**
 * Un texte d'avant une ecriture : celui de la trame, ou d'une scene (reecrite, importee par-dessus,
 * supprimee). Le journal le garde sous l'identifiant de la scene : une scene recreee sous le meme
 * identifiant retrouve ses textes d'avant.
 */
export const texteAvantJeuSchema = z.object({
  id: identifiant,
  quoi: quoiJournalJeuSchema,
  par: auteurJeuSchema,
  scene_cle: z.string().nullable(),
  le: instant,
  texte: z.string(),
});
export type TexteAvantJeu = z.infer<typeof texteAvantJeuSchema>;

/** Les textes d'avant, du plus recent au plus ancien, 20 au plus. */
export const textesAvantJeuSchema = z.object({ textes: z.array(texteAvantJeuSchema) });
export type TextesAvantJeu = z.infer<typeof textesAvantJeuSchema>;

/** La trame (sans `scene`) ou une scene, par son identifiant. */
export const textesAvantRequeteJeuSchema = z.object({ scene: cleJeuSchema.optional() });

// ---------------------------------------------------------------------------------------
// L'export et l'import d'un livre entier en YAML

/** Le livre entier en YAML, tel que le moteur l'ecrit : de quoi le verser dans le depot. */
export const yamlJeuSchema = z.object({ yaml: z.string() });
export type YamlJeu = z.infer<typeof yamlJeuSchema>;

export const importerYamlJeuSchema = z.strictObject({
  yaml: z.string().min(1).max(YAML_LIVRE_MAX),
  /** Vrai : l'apercu seulement, rien n'est ecrit. */
  essai: z.boolean(),
  /**
   * Qui a ecrit les textes du YAML (D9) : le plus souvent le premier jet de Claude, que Nicolas
   * importe faute de jeton ; `nicolas` pour un livre a lui. Le journal note Nicolas, qui importe.
   */
  auteur: auteurJeuSchema,
});
export type ImporterYamlJeu = z.infer<typeof importerYamlJeuSchema>;

export const apercuImportJeuSchema = z.object({
  titre: z.string(),
  /** L'identifiant ecrit dans le YAML ; le livre garde le sien. */
  cle_yaml: z.string(),
  scenes: compte,
  /** Ce que l'import remplacerait : les scenes du livre, et celles qui ont un texte. */
  remplace: z.object({ scenes: compte, ecrites: compte }),
});
export type ApercuImportJeu = z.infer<typeof apercuImportJeuSchema>;

export const importYamlJeuSchema = z.object({
  apercu: apercuImportJeuSchema,
  /** `null` pour un essai ; le livre importe sinon. */
  livre: livreJeuDetailSchema.nullable(),
});
export type ImportYamlJeu = z.infer<typeof importYamlJeuSchema>;

/** Le 400 d'un YAML que le moteur ne lit pas : ses fautes de forme. */
export const yamlIllisibleJeuSchema = z.object({
  error: z.object({
    code: z.literal("yaml_illisible"),
    message: z.string(),
    requestId: z.string().optional(),
  }),
  erreurs: z.array(z.string()),
});
export type YamlIllisibleJeu = z.infer<typeof yamlIllisibleJeuSchema>;

// ---------------------------------------------------------------------------------------
// La publication vers l'appli compagnon

export const publierJeuSchema = z.strictObject({
  /** Vrai pour publier quand meme une structure (pages, codes) qui a change depuis la derniere. */
  confirmer_structure: z.boolean(),
});
export type PublierJeu = z.infer<typeof publierJeuSchema>;

/**
 * L'ecart qui dit que l'ordre des scenes a change : le code de sauvegarde porte le rang de la
 * scene, un code deja note ne menerait plus au meme endroit. Un identifiant de scene n'a pas
 * d'espace : aucune scene ne porte ce nom.
 */
export const ECART_ORDRE_JEU = "ordre des scènes";

/**
 * Une scene dont la page ou les codes changent par rapport a la derniere publication, ou
 * l'ordre des scenes (`scene` vaut alors `ECART_ORDRE_JEU`).
 */
export const ecartStructureJeuSchema = z.object({
  scene: z.string(),
  avant: z.string().nullable(),
  apres: z.string().nullable(),
});
export type EcartStructureJeu = z.infer<typeof ecartStructureJeuSchema>;

/** Le 409 d'une publication dont la structure change : ce qui change, a confirmer. */
export const structureChangeeJeuSchema = z.object({
  error: z.object({
    code: z.literal("structure_changee"),
    message: z.string(),
    requestId: z.string().optional(),
  }),
  ecarts: z.array(ecartStructureJeuSchema),
});
export type StructureChangeeJeu = z.infer<typeof structureChangeeJeuSchema>;

export const publicationFaiteJeuSchema = z.object({ publication: publicationJeuSchema });

/** Ce que sert l'appli compagnon, relu chez elle. */
export const appliJeuSchema = z.discriminatedUnion("etat", [
  z.object({
    etat: z.literal("sert"),
    livre: z.string(),
    titre: z.string(),
    empreinte: z.string(),
    publie_le: instant,
  }),
  z.object({ etat: z.literal("rien") }),
  z.object({ etat: z.literal("injoignable"), message: z.string() }),
]);
export type AppliJeu = z.infer<typeof appliJeuSchema>;

// ---------------------------------------------------------------------------------------
// Les routes du PC (jeton `LIVRES_JEUX_TOKEN`)

/** Reponse de `POST /api/livres-jeux/machine/ping` : le jeton du PC est accepte. */
export const pingLivresJeuxSchema = z.object({
  module: z.literal("livres-jeux"),
  ok: z.literal(true),
});
export type PingLivresJeux = z.infer<typeof pingLivresJeuxSchema>;

/** Un livre entier, pour Claude : les reglages, la trame et chaque scene avec ses versions. */
export const livreJeuMachineSchema = z.object({
  livre: livreJeuSchema,
  scenes: z.array(sceneJeuSchema),
});
export type LivreJeuMachine = z.infer<typeof livreJeuMachineSchema>;

/** Le premier jet : un livre entier en YAML, refuse des que Nicolas a ecrit une scene. */
export const importMachineJeuSchema = z.strictObject({
  yaml: z.string().min(1).max(YAML_LIVRE_MAX),
});
export type ImportMachineJeu = z.infer<typeof importMachineJeuSchema>;
